import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Divider, Input, Popconfirm, Row, Space, Tag, Typography } from 'antd';
import freeEmailDomains from 'free-email-domains';
import {
    TbRosetteDiscountCheckFilled,
    TbWorldCheck,
    TbWorldMinus,
    TbWorldQuestion,
    TbWorldUpload,
    TbWorldX,
    TbX,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { workspaceService } from '@/services/database';
import {
    EmptySection,
    EnrichedAnchorTag,
    LearnMoreFooter,
    VirtualizedList as List,
    PlanTag,
    PlatformAlert,
    SmallTooltip,
} from '@/shared';
import DomainConnectSetupButton from '@/shared/components/domains/DomainConnectSetupButton';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useWorkspaceStore } from '@/stores';
import { WorkspaceVerifiedDomain } from '@/types';
import { checkForValidDomain, companyFavicon } from '@/utilities';

import Metadata from './meta';

const normalizeDomain = (value: string) =>
    value
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .split('/')[0]
        .toLowerCase();

const getStatusLabel = (status: WorkspaceVerifiedDomain['status']) => {
    if (status === 'Approved') {
        return 'Verified';
    }

    if (status === 'Failed') {
        return 'Failed';
    }

    return 'Pending Verification';
};

const getStatusColor = (status: WorkspaceVerifiedDomain['status']) => {
    if (status === 'Approved') {
        return 'green';
    }

    if (status === 'Failed') {
        return 'red';
    }

    return 'gold';
};

const getStatusIcon = (status: WorkspaceVerifiedDomain['status']) => {
    if (status === 'Approved') {
        return <TbRosetteDiscountCheckFilled style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />;
    }

    if (status === 'Failed') {
        return <TbWorldX style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />;
    }

    return <TbWorldQuestion style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />;
};

const getStatusTooltip = (domain: WorkspaceVerifiedDomain) => {
    if (domain.status === 'Approved') {
        return 'This domain is verified and can be used for SAML SSO domain ownership.';
    }

    if (domain.status === 'Failed') {
        return 'We could not verify this domain. Review the DNS record and retry verification.';
    }

    return 'This domain is pending verification. Add the DNS record below, then verify the domain.';
};

const VerifiedDomains: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [verifyingDomainId, setVerifyingDomainId] = useState<string>();
    const [removingDomainId, setRemovingDomainId] = useState<string>();
    const [domains, setDomains] = useState<WorkspaceVerifiedDomain[]>([]);
    const [domain, setDomain] = useState('');
    const [domainStatus, setDomainStatus] = useState<'warning' | ''>('');

    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const workspaceId = workspace?.id;
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();

    const normalizedDomain = useMemo(() => normalizeDomain(domain), [domain]);
    const enterpriseEnabled = hasFeature('verifiedDomains');
    const canCreateDomain =
        enterpriseEnabled && normalizedDomain !== '' && checkForValidDomain(normalizedDomain) && !creating;

    const fetchDomains = useCallback(async () => {
        if (!workspaceId) {
            setDomains([]);
            setLoading(false);

            return;
        }

        try {
            setLoading(true);

            const nextDomains = await workspaceService.getVerifiedDomains(workspaceId);

            setDomains(nextDomains);
        } catch (error) {
            captureException(error);

            toast.error('Unable to load verified domains');
        } finally {
            setLoading(false);
        }
    }, [workspaceId]);

    useEffect(() => {
        void fetchDomains();
    }, [fetchDomains]);

    const handleCreateDomain = async () => {
        if (!workspace?.id || !canCreateDomain) {
            return;
        }

        if (freeEmailDomains.includes(normalizedDomain)) {
            toast.error('Generic email domains cannot be verified for a workspace.');

            return;
        }

        try {
            setCreating(true);

            const createdDomain = await workspaceService.createVerifiedDomain(workspace.id, {
                domain: normalizedDomain,
            });

            toast('Domain Added', {
                icon: <TbWorldCheck />,
                description: createdDomain.url,
            });

            setDomains((currentDomains) => [createdDomain, ...currentDomains]);
            setDomain('');
            setDomainStatus('');
        } catch (error) {
            captureException(error);

            toast.error('Unable to add verified domain');
        } finally {
            setCreating(false);
        }
    };

    const handleVerifyDomain = async (selectedDomain: WorkspaceVerifiedDomain) => {
        if (!workspace?.id || !selectedDomain.id) {
            return;
        }

        try {
            setVerifyingDomainId(selectedDomain.id);

            const verifiedDomain = await workspaceService.verifyDomain(workspace.id, selectedDomain.id);

            toast('Domain Verification Checked', {
                icon: <TbWorldCheck />,
                description: verifiedDomain.url,
            });

            setDomains((currentDomains) =>
                currentDomains.map((currentDomain) =>
                    currentDomain.id === verifiedDomain.id ? verifiedDomain : currentDomain,
                ),
            );
        } catch (error) {
            captureException(error);

            toast.error('Unable to verify domain');
        } finally {
            setVerifyingDomainId(undefined);
        }
    };

    const handleRemoveDomain = async (selectedDomain: WorkspaceVerifiedDomain) => {
        if (!workspace?.id || !selectedDomain.id) {
            return;
        }

        try {
            setRemovingDomainId(selectedDomain.id);

            await workspaceService.deleteVerifiedDomain(workspace.id, selectedDomain.id);

            toast('Domain Removed', {
                icon: <TbWorldMinus />,
                description: selectedDomain.url,
            });

            setDomains((currentDomains) =>
                currentDomains.filter((currentDomain) => currentDomain.id !== selectedDomain.id),
            );
        } catch (error) {
            captureException(error);

            toast.error('Unable to remove verified domain');
        } finally {
            setRemovingDomainId(undefined);
        }
    };

    return (
        <>
            <Metadata />
            <Typography.Title level={4}>
                Verified Domains
                <PlanTag plan='enterprise' style={{ marginLeft: '0.75rem', verticalAlign: '0.25rem' }} />
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Manage verified domains for SAML SSO & workspace access.
            </Typography.Paragraph>
            <PlatformAlert
                message={<>SAML SSO required for Verified Domains</>}
                status='warning'
                description={
                    <>
                        Verified domains are managed through{' '}
                        <EnrichedAnchorTag label={'WorkOS'} url='https://workos.com' /> and are required before SAML SSO
                        can be enforced for a workspace domain.
                    </>
                }
                styles={{ marginBottom: '1rem' }}
            />
            <Divider style={{ margin: '1rem 0' }} />
            <Row gutter={[8, 0]}>
                <Col flex='auto'>
                    <Space.Compact style={{ width: '100%' }}>
                        <Space.Addon style={{ padding: '0 0.5rem', color: token.colorTextTertiary }}>
                            https://
                        </Space.Addon>
                        <Input
                            value={domain}
                            disabled={!enterpriseEnabled}
                            status={
                                domainStatus === '' || checkForValidDomain(normalizedDomain) || domain === ''
                                    ? undefined
                                    : domainStatus
                            }
                            onChange={(event) => {
                                const nextDomain = event.target.value;

                                setDomain(nextDomain);
                                setDomainStatus(
                                    nextDomain === '' || checkForValidDomain(normalizeDomain(nextDomain))
                                        ? ''
                                        : 'warning',
                                );
                            }}
                            onPressEnter={() => {
                                void handleCreateDomain();
                            }}
                            placeholder='example.com...'
                        />
                    </Space.Compact>
                </Col>
                <Col flex='none'>
                    <Button
                        type='primary'
                        block
                        loading={creating}
                        disabled={!canCreateDomain}
                        onClick={() => {
                            void handleCreateDomain();
                        }}
                    >
                        <TbWorldUpload size='1rem' style={{ verticalAlign: '-0.2rem' }} />
                        Add Domain
                    </Button>
                </Col>
            </Row>
            <div style={{ marginBottom: '0.75rem', marginTop: '0.5rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Verified Domains</small>
            </div>
            {domains.length !== 0 && !loading ? (
                <List
                    style={{ marginBottom: '0.75rem', maxHeight: '18rem' }}
                    dataSource={domains}
                    size='small'
                    renderItem={(domainRecord: WorkspaceVerifiedDomain) => {
                        const verificationHost = domainRecord.verificationPrefix
                            ? `${domainRecord.verificationPrefix}.${domainRecord.url}`
                            : domainRecord.url;

                        return (
                            <List.Item style={{ padding: '0.75rem 0' }}>
                                <List.Item.Meta
                                    avatar={
                                        <Avatar
                                            shape='square'
                                            size='small'
                                            src={companyFavicon(domainRecord.url)}
                                            icon={<TbWorldCheck />}
                                        />
                                    }
                                    title={
                                        <>
                                            {domainRecord.url}
                                            <SmallTooltip placement='right' title={getStatusTooltip(domainRecord)}>
                                                <Tag
                                                    color={getStatusColor(domainRecord.status)}
                                                    style={{ marginLeft: '1.5rem', borderColor: 'transparent' }}
                                                >
                                                    {getStatusIcon(domainRecord.status)}
                                                    {getStatusLabel(domainRecord.status)}
                                                </Tag>
                                            </SmallTooltip>
                                        </>
                                    }
                                    description={
                                        domainRecord.status !== 'Approved' && domainRecord.verificationToken ? (
                                            <div style={{ marginTop: '0.5rem' }}>
                                                <small style={{ color: token.colorTextTertiary }}>
                                                    Add this TXT record to verify ownership:
                                                </small>
                                                <div
                                                    style={{
                                                        display: 'grid',
                                                        gap: '0.25rem',
                                                        marginTop: '0.375rem',
                                                    }}
                                                >
                                                    <small style={{ color: token.colorTextSecondary }}>
                                                        Type: <code>TXT</code>
                                                    </small>
                                                    <small style={{ color: token.colorTextSecondary }}>
                                                        Host: <code>{verificationHost}</code>
                                                    </small>
                                                    <small style={{ color: token.colorTextSecondary }}>
                                                        Value: <code>{domainRecord.verificationToken}</code>
                                                    </small>
                                                </div>
                                            </div>
                                        ) : undefined
                                    }
                                />
                                <Space size={4}>
                                    {workspaceId && domainRecord.status !== 'Approved' && (
                                        <>
                                            <DomainConnectSetupButton
                                                disabled={Boolean(verifyingDomainId || removingDomainId)}
                                                prepareIntent={() =>
                                                    workspaceService.prepareVerifiedDomainDomainConnect(
                                                        workspaceId,
                                                        domainRecord.id,
                                                    )
                                                }
                                                onReturn={() => handleVerifyDomain(domainRecord)}
                                            />
                                            <Button
                                                style={{ verticalAlign: 0 }}
                                                size='small'
                                                type='dashed'
                                                loading={verifyingDomainId === domainRecord.id}
                                                onClick={() => {
                                                    void handleVerifyDomain(domainRecord);
                                                }}
                                            >
                                                Verify
                                            </Button>
                                        </>
                                    )}
                                    <Popconfirm
                                        overlayStyle={{ maxWidth: 300 }}
                                        title='Are you sure you want to remove this verified domain?'
                                        onConfirm={() => {
                                            void handleRemoveDomain(domainRecord);
                                        }}
                                        okText='Yes'
                                        cancelText='No'
                                        placement='right'
                                    >
                                        <Button
                                            style={{ verticalAlign: 0 }}
                                            size='small'
                                            type='dashed'
                                            loading={removingDomainId === domainRecord.id}
                                            key='removeDomain'
                                            icon={
                                                <TbX
                                                    style={{
                                                        verticalAlign: '-0.15rem',
                                                        color: token.colorTextTertiary,
                                                    }}
                                                />
                                            }
                                        />
                                    </Popconfirm>
                                </Space>
                            </List.Item>
                        );
                    }}
                />
            ) : loading ? (
                <BaseSkeleton active round />
            ) : (
                <EmptySection
                    height='25rem'
                    icon={<TbWorldCheck style={{ color: token.colorTextTertiary }} />}
                    label='No Verified Domains'
                    description='Add verified domains to scope permissioned users to your workspace.'
                />
            )}
            <Divider style={{ margin: '1rem 0' }} />
            <LearnMoreFooter topic={<>verified domains</>} />
        </>
    );
};

export default VerifiedDomains;
